import { Tag, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import { DataColumn } from "../../controls/Data.default";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import { InputObject } from "../../controls/default";
import { buildSearchQuery, comboboxValue, dateRangeValue, numberValue, QueryFieldMapping } from "../../code/search-query/query-mapper";
import { createSearchRequest } from "../../hooks/useSearchRequest";
import { getCompanies, getTenants } from "../../repository/data.repository";

export interface InvoicesViewForm {
  readonly company: InputObject;
  readonly tenant: InputObject;
  readonly description: string;
  readonly isPaid: boolean;
  readonly dueDate: Dayjs[];
  readonly totalAmountMin: number;
  readonly totalAmountMax: number;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
}

export const invoicesViewColumns: DataColumn[] = [
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
    defaultSort: "asc",
  },
  {
    title: "Tenant",
    key: "tenantName",
    width: 192,
    sort: true,
  },
  {
    title: "Description",
    key: "description",
    width: 192,
    sort: true,
  },
  {
    title: "Total Amount",
    key: "totalAmount",
    width: 192,
    sort: true,
  },
  {
    title: "Paid",
    key: "isPaid",
    width: 120,
    sort: true,
    // Colored status tag (mirrors the Incidents grid) — paid green / unpaid red.
    transform: (value: boolean) => (
      <Tag color={value ? "green" : "red"}>{value ? "Paid" : "Unpaid"}</Tag>
    ),
  },
  {
    title: "Due Date",
    key: "dueDate",
    width: 168,
    sort: true,
    // Flag unpaid invoices that are past their due date.
    transform: (value: string, record?: any) => {
      if (!value) return "—";
      const formatted = dayjs(value).format("DD/MM/YYYY");
      const overdue =
        record && !record.isPaid && dayjs(value).isBefore(dayjs(), "day");
      return overdue ? (
        <span className="whitespace-nowrap">
          <Typography.Text type="danger">{formatted}</Typography.Text>{" "}
          <Tag color="red">Overdue</Tag>
        </span>
      ) : (
        formatted
      );
    },
  },
  {
    title: "Items",
    key: "itemsCount",
    width: 192,
    sort: true,
    type: "badge",
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 192,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Created By",
    key: "createdByUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Updated On",
    key: "updatedOn",
    width: 192,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Updated By",
    key: "updatedByUserUserName",
    width: 192,
    sort: true,
  },
];

const invoicesQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "tenant", queryField: "tenantUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "description", operation: QueryOperation.Contains },
  { formField: "isPaid", operation: QueryOperation.Equal },
  // Due date is a range (Between) rather than an exact match — more useful, and avoids matching a
  // DateOnly column against a single exact value.
  { formField: "dueDate", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "totalAmountMin", queryField: "totalAmount", operation: QueryOperation.GreaterThanOrEqual, transform: numberValue },
  { formField: "totalAmountMax", queryField: "totalAmount", operation: QueryOperation.LessThanOrEqual, transform: numberValue },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "createdBy", queryField: "createdByUserUserName" },
  { formField: "updatedOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "updatedBy", queryField: "updatedByUserUserName" },
];

export const mapInvoicesViewQuery = (values: InvoicesViewForm): SearchQuery =>
  buildSearchQuery(values, invoicesQueryMappings);

export const invoicesSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        { type: "combobox", name: "company", label: "Company", request: createSearchRequest(getCompanies, "name") },
        { type: "combobox", name: "tenant", label: "Tenant", request: createSearchRequest(getTenants, "name") },
        { type: "textbox", name: "description", label: "Description" },
        { type: "togglebox", name: "isPaid", label: "Paid" },
        { type: "rangepicker", name: "dueDate", label: "Due Date" },
        { type: "numberbox", name: "totalAmountMin", label: "Min Amount" },
        { type: "numberbox", name: "totalAmountMax", label: "Max Amount" },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
        { type: "textbox", name: "createdBy", label: "Created By" },
        { type: "rangetimepicker", name: "updatedOn", label: "Updated On" },
        { type: "textbox", name: "updatedBy", label: "Updated By" },
      ],
    },
  ],
};
